import { Card, CardContent, Avatar } from "@mui/material";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import PropTypes from "prop-types";
import { blue, green, red, purple } from "@mui/material/colors";

const StatCard = ({ title, stats, horizontal = false, action = null }) => {
  const colors = [blue[500], green[500], red[500], purple[500]];

  return (
    <Card
      sx={{
        borderRadius: "16px",
        boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: "0px 6px 16px rgba(0,0,0,0.15)",
        },
      }}
    >
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        {/* Card Title */}
        <ArgonBox
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1.5 }}
        >
          <ArgonTypography
            variant="h6"
            fontWeight="bold"
            color="dark"
            sx={{ textAlign: "center", fontSize: "1rem", flex: 1 }}
          >
            {title}
          </ArgonTypography>
          {action ? <ArgonBox sx={{ ml: 1 }}>{action}</ArgonBox> : null}
        </ArgonBox>

        {/* Stats Container */}
        <ArgonBox
          display="flex"
          flexDirection={horizontal ? "row" : "column"}
          flexWrap={horizontal ? "wrap" : "nowrap"}
          gap={horizontal ? 2 : 0}
          justifyContent={horizontal ? "space-evenly" : "flex-start"}
        >
          {/* Stats Rows */}
          {stats.map((item, index) => (
            <ArgonBox
              key={index}
              display="flex"
              flexDirection={horizontal ? "column" : "row"}
              alignItems="center"
              justifyContent="center"
              mb={horizontal ? 0 : index === stats.length - 1 ? 0 : 1.5}
              p={horizontal ? 2 : 1}
              sx={{
                borderRadius: horizontal ? "12px" : "10px",
                backgroundColor: horizontal ? "transparent" : "rgba(245, 245, 245, 0.6)",
                flex: horizontal ? "1 1 0" : "none",
                minWidth: horizontal ? "100px" : "auto",
                maxWidth: horizontal ? "150px" : "none",
                gap: horizontal ? 0.8 : 1,
                transition: "all 0.2s ease",
                "&:hover": horizontal
                  ? {
                      backgroundColor: "rgba(245, 245, 245, 0.4)",
                      transform: "translateY(-2px)",
                    }
                  : {},
              }}
            >
              {/* Icon Circle */}
              <Avatar
                sx={{
                  bgcolor: colors[index % colors.length],
                  width: horizontal ? 36 : 32,
                  height: horizontal ? 36 : 32,
                  fontSize: horizontal ? "0.85rem" : "0.8rem",
                  fontWeight: "bold",
                  mb: horizontal ? 0.5 : 0,
                  ml: horizontal ? 0 : 0,
                  boxShadow: horizontal ? "0 2px 6px rgba(0,0,0,0.12)" : "none",
                }}
              >
                {index + 1}
              </Avatar>

              {/* Label */}
              <ArgonTypography
                variant="button"
                color="text"
                fontWeight={horizontal ? "bold" : "medium"}
                sx={{
                  flex: horizontal ? "none" : 1,
                  ml: horizontal ? 0 : 1.5,
                  mr: horizontal ? 0 : 1,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  minWidth: 0,
                  fontSize: horizontal ? "0.7rem" : "0.875rem",
                  textAlign: horizontal ? "center" : "left",
                  textTransform: "uppercase",
                  letterSpacing: "0.3px",
                  color: horizontal ? "#6B7280" : "text",
                }}
              >
                {item.label}
              </ArgonTypography>

              {/* Value */}
              <ArgonTypography
                variant="body1"
                color="dark"
                fontWeight="bold"
                sx={{
                  fontSize: horizontal
                    ? { xs: "1.1rem", sm: "1.25rem", md: "1.4rem" }
                    : { xs: "0.8rem", sm: "0.9rem", md: "1rem" },
                  textAlign: "center",
                  flexShrink: 0,
                  whiteSpace: "nowrap",
                }}
              >
                {item.value}
              </ArgonTypography>
            </ArgonBox>
          ))}
        </ArgonBox>
      </CardContent>
    </Card>
  );
};

StatCard.propTypes = {
  title: PropTypes.string.isRequired,
  horizontal: PropTypes.bool,
  action: PropTypes.node,
  stats: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    })
  ).isRequired,
};

export default StatCard;
